/**
 * Een gelezen tekst terugkijken: de tekst, of de toets met de antwoorden van
 * de leerling. Bij de toets staat de tekst links; klik op een vraag en de
 * passage waar het antwoord staat, wordt geel.
 */
import React, { useState } from 'react';
import { Check, ChevronLeft, FileText } from 'lucide-react';
import { OpgemaakteTekst } from '../../../components/OpgemaakteTekst';
import { AnnaMariaZegt } from '../onderdelen/AnnaMaria';
import { Nummer } from '../onderdelen/Nummer';

export function Terugkijken({ gelezen, onTerug }) {
  const [tab, setTab] = useState('tekst');          // tekst of toets
  const [gekozen, setGekozen] = useState(null);     // de vraag waarvan de passage gemarkeerd is

  const kiesTab = (nieuw) => { setTab(nieuw); setGekozen(null); };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
      <button className="btn btn-secondary" onClick={onTerug} style={{ marginBottom: 16 }}>
        <ChevronLeft size={16} /> Terug
      </button>

      <div className="card" style={{ padding: 24 }}>
        <h1 style={{ marginBottom: 8 }}>{gelezen.text_titel}</h1>
        <p style={{ color: 'var(--tekst-3)', marginBottom: 24 }}>
          Gelezen op {gelezen.completed_at}
          {gelezen.mc_score !== null && gelezen.mc_score !== undefined && (
            <span style={{ marginLeft: 16 }}>
              Score: <strong>{gelezen.mc_score}/{gelezen.mc_total} ({gelezen.mc_percentage}%)</strong>
            </span>
          )}
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '1px solid var(--lijn)', paddingBottom: 12 }}>
          <button className={`btn ${tab === 'tekst' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => kiesTab('tekst')}>
            <FileText size={16} /> Tekst
          </button>
          <button className={`btn ${tab === 'toets' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => kiesTab('toets')}>
            <Check size={16} /> Toets
          </button>
        </div>

        {tab === 'tekst' && (
          <div>
            {gelezen.paragraphs.map((p, i) => (
              <div key={p.id} style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
                <Nummer n={i + 1} grootte={24} />
                <div style={{ lineHeight: 1.7, margin: 0 }}><OpgemaakteTekst tekst={p.inhoud} /></div>
              </div>
            ))}
          </div>
        )}

        {tab === 'toets' && <Toets gelezen={gelezen} gekozen={gekozen} onKies={setGekozen} />}
      </div>
    </div>
  );
}

function Toets({ gelezen, gekozen, onKies }) {
  const passage = gekozen !== null ? gelezen.mc_results[gekozen]?.relevante_passage : null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
      <div style={{ background: 'var(--vlak)', borderRadius: 8, padding: 20, maxHeight: 600, overflowY: 'auto' }}>
        <h4 style={{ marginBottom: 16, color: 'var(--tekst-3)' }}>De tekst</h4>
        {gelezen.paragraphs.map((p, i) => (
          <div key={p.id} style={{ marginBottom: 12, display: 'flex', gap: 10 }}>
            <Nummer n={i + 1} grootte={20} />
            <div style={{ lineHeight: 1.6, margin: 0, fontSize: 14 }}>
              <OpgemaakteTekst tekst={p.inhoud} markeer={passage} />
            </div>
          </div>
        ))}
      </div>

      <div style={{ maxHeight: 600, overflowY: 'auto' }}>
        {gelezen.mc_results.length > 0 && (
          <>
            <h3 style={{ marginBottom: 16 }}>Vragen over de tekst</h3>
            <p style={{ fontSize: 13, color: 'var(--tekst-3)', marginBottom: 16 }}>
              Klik op een vraag om de relevante passage in de tekst te markeren.
            </p>
            {gelezen.mc_results.map((mc, i) => (
              <div
                key={i}
                style={{
                  marginBottom: 16,
                  padding: 16,
                  background: mc.is_correct ? 'var(--goed-vlak)' : 'var(--fout-vlak)',
                  borderRadius: 8,
                  borderLeft: `4px solid ${mc.is_correct ? 'var(--goed)' : 'var(--fout)'}`,
                  cursor: mc.relevante_passage ? 'pointer' : 'default',
                  outline: gekozen === i ? '3px solid var(--accent)' : 'none',
                }}
                onClick={() => mc.relevante_passage && onKies(gekozen === i ? null : i)}
              >
                <p style={{ fontWeight: 500, marginBottom: 8 }}>Vraag {i + 1}: {mc.vraag}</p>
                <p style={{ marginBottom: 4 }}>
                  Jouw antwoord: <strong>{mc.gegeven_antwoord}</strong> - {mc.opties[mc.gegeven_antwoord]}
                </p>
                {!mc.is_correct && (
                  <p style={{ color: 'var(--goed-tekst)' }}>
                    Correct antwoord: <strong>{mc.correct_antwoord}</strong> - {mc.opties[mc.correct_antwoord]}
                  </p>
                )}
                {mc.uitleg && <p style={{ fontSize: 13, color: 'var(--tekst-3)', marginTop: 8, fontStyle: 'italic' }}>{mc.uitleg}</p>}
                {mc.relevante_passage && <p style={{ fontSize: 12, color: 'var(--accent)', marginTop: 8 }}>📖 Klik om passage te markeren</p>}
              </div>
            ))}
          </>
        )}

        {gelezen.open_results.length > 0 && (
          <>
            <h3 style={{ marginTop: 24, marginBottom: 16 }}>Open vragen</h3>
            {gelezen.open_results.map((open, i) => (
              <div key={i} style={{ marginBottom: 16, padding: 16, background: 'var(--vlak)', borderRadius: 8, borderLeft: '4px solid var(--accent)' }}>
                <p style={{ fontWeight: 500, marginBottom: 8 }}>Vraag {i + 1}: {open.vraag}</p>
                <p style={{ marginBottom: 12 }}>Jouw antwoord: <em>{open.antwoord}</em></p>
                {open.feedback && <AnnaMariaZegt>{open.feedback}</AnnaMariaZegt>}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
